/**
 * Shared helpers for parsing raw interview transcript strings into
 * role/text pairs. Used by both ResponsesPanel (full Sheet view) and
 * ResponsesZone (inline preview) so the two views parse and display
 * answers identically.
 *
 * Lives in its own module so React Fast Refresh can still work for
 * ResponsesPanel — mixing component exports with helper exports in
 * the same file causes Vite's HMR plugin to fall back to full page
 * reloads.
 */

/** Strip metadata markers such as *** Sent as audio ***, etc. */
export function cleanMessageText(text: string): string {
  return text
    .replace(/\*{2,3}\s*Sent as (?:audio|video|image|file)\s*\*{2,3}/gi, '')
    .replace(/\*{2,3}\s*Audio message\s*\*{2,3}/gi, '')
    .replace(/\*{2,3}\s*Video message\s*\*{2,3}/gi, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Parse all messages from a raw transcript string into role/text pairs. */
export function parseMessages(rawText: string): Array<{ role: string; text: string }> {
  const messageRegex = /(?:^|\n)((?:Interviewer|Participant|Assistant|User):)\s*/g;
  const messages: { role: string; text: string }[] = [];
  let lastIdx = 0;
  let lastRole = '';
  let m: RegExpExecArray | null;

  // eslint-disable-next-line no-cond-assign
  while ((m = messageRegex.exec(rawText)) !== null) {
    if (lastRole) {
      const cleaned = cleanMessageText(rawText.slice(lastIdx, m.index));
      if (cleaned) messages.push({ role: lastRole, text: cleaned });
    }
    lastRole = m[1].replace(':', '');
    lastIdx = m.index + m[0].length;
  }
  if (lastRole) {
    const cleaned = cleanMessageText(rawText.slice(lastIdx));
    if (cleaned) messages.push({ role: lastRole, text: cleaned });
  }

  return messages.length > 0
    ? messages
    : [{ role: 'Participant', text: cleanMessageText(rawText) }];
}

/** Extract the first participant answer text from a raw transcript string. */
export function extractParticipantAnswer(rawText: string): string {
  const messages = parseMessages(rawText);
  const participant = messages.find(
    (msg) => msg.role !== 'Interviewer' && msg.role !== 'Assistant',
  );
  return participant?.text || cleanMessageText(rawText);
}
